import React from "react";

import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export interface IServerLinkOption {
  id: string;
  value: string;
  label: string;
  userCustomerId?: number;
  confidentialityId?: number;
  persisted?: boolean;
  name?: string;
  email?: string;
  cpf?: string | null;
}

interface ServerLinkDrawerContentProps {
  inputValue: string;
  loading: boolean;
  options: IServerLinkOption[];
  selectedServers: IServerLinkOption[];
  fieldError: boolean;
  onInputChange: (value: string) => void;
  onSelect: (server: IServerLinkOption | null) => void;
  onRemove: (server: IServerLinkOption) => void;
}

const normalizeEmail = (value?: string) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeCpf = (value?: string | null) =>
  String(value ?? "").replace(/\D/g, "");

const isSameServer = (
  option: IServerLinkOption,
  selectedServer: IServerLinkOption,
) => {
  const optionEmail = normalizeEmail(option.email);
  const selectedEmail = normalizeEmail(selectedServer.email);
  const optionCpf = normalizeCpf(option.cpf);
  const selectedCpf = normalizeCpf(selectedServer.cpf);

  return (
    option.id === selectedServer.id ||
    (option.userCustomerId &&
      selectedServer.userCustomerId &&
      option.userCustomerId === selectedServer.userCustomerId) ||
    (optionEmail && optionEmail === selectedEmail) ||
    (optionCpf && optionCpf === selectedCpf)
  );
};

export const ServerLinkDrawerContent: React.FC<
  ServerLinkDrawerContentProps
> = ({
  inputValue,
  loading,
  options,
  selectedServers,
  fieldError,
  onInputChange,
  onSelect,
  onRemove,
}) => {
  const { t } = useTranslation();
  const availableOptions = options.filter(
    (option) =>
      !selectedServers.some((selectedServer) =>
        isSameServer(option, selectedServer),
      ),
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "28px",
        width: "100%",
        paddingTop: "10px",
      }}
    >
      <Box>
        <Typography
          component="label"
          sx={{
            display: "block",
            color: "#344054",
            fontFamily: "Inter, sans-serif",
            fontSize: "16px",
            fontWeight: 600,
            marginBottom: "10px",
          }}
        >
          {t("audiences.serverLink.searchLabel")}
        </Typography>
        <Autocomplete<IServerLinkOption, false, false, false>
          value={null}
          inputValue={inputValue}
          loading={loading}
          options={availableOptions}
          filterOptions={(options) => options}
          noOptionsText={t("audiences.serverLink.notFound")}
          loadingText={t("audiences.serverLink.loading")}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onInputChange={(_, newValue, reason) => {
            if (reason === "reset") return;
            onInputChange(newValue);
          }}
          onChange={(_, option) => onSelect(option)}
          renderInput={(params) => (
            <TextField
              {...params}
              error={fieldError}
              helperText={
                fieldError ? t("audiences.serverLink.selectServer") : ""
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  minHeight: "48px",
                  "& fieldset": {
                    borderColor: fieldError ? "#d32f2f" : "#d1dbe3",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: fieldError ? "#d32f2f" : "#b9c6d0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: fieldError ? "#d32f2f" : "#5fa8ed",
                  },
                },
              }}
            />
          )}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            color: "#344054",
            fontFamily: "Inter, sans-serif",
            fontSize: "16px",
            fontWeight: 600,
            marginBottom: "14px",
          }}
        >
          {t("audiences.serverLink.linkedServers")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          {selectedServers.map((server) => (
            <Chip
              key={server.id}
              label={server.email || server.cpf || server.name || server.label}
              onDelete={() => onRemove(server)}
              sx={{
                maxWidth: "100%",
                height: "40px",
                borderRadius: "20px",
                border: "2px solid #96c9ff",
                backgroundColor: "#ffffff",
                color: "#3f8ee8",
                fontFamily: "Inter, sans-serif",
                fontSize: "15px",
                fontWeight: 700,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingLeft: "16px",
                },
                "& .MuiChip-deleteIcon": {
                  color: "#6aa7e8",
                  fontSize: "26px",
                  marginRight: "6px",
                  "&:hover": {
                    color: "#3f8ee8",
                  },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ServerLinkDrawerContent;
